import React from 'react';
import { cloneElement, useEffect, type ReactNode } from 'react';
import { cn } from '../utils/cn';
import { PUBLIC_PATH } from '../shared/config/routes-config';

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children?: ReactNode; // Body content
  footerContent?: ReactNode; // Footer buttons
  headerContent?: ReactNode; // Custom content passed to header
  closeIcon?: ReactNode;
  contentClassName?: string; // Custom class for content
  containerClassName?: string; // Custom class for container
  headerAction?: ReactNode;
  closeIconPosition?: 'left' | 'right';
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  footerContent,
  headerContent,
  headerAction,
  closeIcon,
  contentClassName,
  containerClassName,
  closeIconPosition = 'left'
}) => {
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50"
      // onClick={handleBackdropClick}
      // onKeyDown={handleBackdropKeyDown}
      aria-modal="true"
      tabIndex={-1}
    >
      <div
        className={cn(
          'relative flex h-full w-full flex-col items-start bg-white shadow-lg lg:h-[85vh] lg:w-[720px] lg:rounded-2xl',
          containerClassName
        )}
      >
        {headerContent && (
          <div className="flex items-center justify-between self-stretch border-b border-b-neutral-50 bg-white px-6 py-6 lg:rounded-tl-2xl lg:rounded-tr-2xl">
            {closeIconPosition === 'left' && (
              <div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex items-center gap-2.5 rounded-full p-[0.4375rem] hover:bg-gray-100"
                  aria-label="Close drawer"
                >
                  {(closeIcon &&
                    cloneElement(closeIcon as React.ReactElement<any>, {
                      className: (closeIcon as any)?.props?.className
                    })) || (
                    <img src={`${PUBLIC_PATH}/assets/svgs/close.svg`} alt="Close drawer icon" />
                  )}
                </button>
              </div>
            )}
            {closeIconPosition === 'right' && <div />}
            <div className="text-center font-gilroy-bold font-normal leading-5 text-neutral-500 lg:w-1/2 lg:text-xl">
              {headerContent}
            </div>

            {closeIconPosition === 'right' && (
              <div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex items-center gap-2.5 rounded-full p-[0.4375rem] hover:bg-gray-100"
                  aria-label="Close modal"
                >
                  {closeIcon || (
                    <img src={`${PUBLIC_PATH}/assets/svgs/close.svg`} alt="close Modal Icon" />
                  )}
                </button>
              </div>
            )}
            {closeIconPosition === 'left' && !headerAction && <div />}
            {headerAction && headerAction}
          </div>
        )}
        {!headerContent && !footerContent && (
          <button
            type="button"
            className="absolute right-6 top-6 z-30 rounded-md bg-white p-2"
            onClick={onClose}
          >
            <img src={`${PUBLIC_PATH}/assets/svgs/close.svg`} alt="close Modal Icon" />
          </button>
        )}

        <div
          className={cn(
            'flex h-full flex-col items-start gap-10 self-stretch overflow-y-auto px-6 py-10 lg:h-auto',
            contentClassName
          )}
        >
          {children}
        </div>

        {footerContent && (
          <div className="mt-auto flex items-center justify-end gap-2.5 self-stretch rounded-br-2xl border-t border-t-neutral-50 bg-white px-6 py-4 lg:rounded-bl-2xl">
            {footerContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
